import {
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PickerProps } from '../__internal__';
import { Select } from '../../Select';
import { Flow } from '../../Layouts/Flow';
import { withLabels } from '../../withLabels';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';
import { isThaiLocale } from '../../../locale/localeUtils';
import { IntegerInputRef } from '../__internal__/';
import { PickerRef } from './PickerRef';
import { YearInput } from './YearInput';
import {
  MIN_GREGORIAN_CALENDAR_DATE,
  MAX_GREGORIAN_CALENDAR_DATE,
} from '../../Calendar/GregorianCalendarFacts';
import {
  getYearOrder,
  convertThaiYearToGregorianYear,
  getDateByWeekNumber,
  getYearOfWeek,
  getNumberOfWeeks,
  getWeekNumber,
} from '../DateUtil';
import { useDefaultDateTimeFormat } from '../useDefaultDateTimeFormat';
import { useMounted } from '../../../hooks/useMounted';
import { postfixTestId } from '../../../util/testId';
import bundle from '../NovaComponents-DatePicker-gui-icu.properties';

import '../DatePicker.css';

const SelectWithLabels = withLabels(Select);
const useTextLocalizer = createUseTextLocalizer(bundle);

export const WeekPicker = forwardRef<PickerRef, PickerProps>(function WeekPicker(
  { minDate = MIN_GREGORIAN_CALENDAR_DATE, maxDate = MAX_GREGORIAN_CALENDAR_DATE, ...props },
  ref
) {
  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const mountedRef = useMounted();
  const yearOrder = useMemo(() => getYearOrder(locale), [locale]);
  const numberOfWeeks = getNumberOfWeeks(props.value);
  const weekNumber = getWeekNumber(props.value);
  const year = getYearOfWeek(props.value, locale);

  const minWeekAllowed = year === getYearOfWeek(minDate, locale) ? getWeekNumber(minDate) : 1;
  const maxWeekAllowed =
    year === getYearOfWeek(maxDate, locale) ? getWeekNumber(maxDate) : numberOfWeeks;

  const [isWeekValid, setIsWeekValid] = useState(true);

  const defaultFormat = useDefaultDateTimeFormat({
    type: 'week',
  });

  const minMaxWeekErrorMsg = localize('minmax.week.error.msg', {
    min: defaultFormat(minDate),
    max: defaultFormat(maxDate),
  });

  const validateWeekValue = useCallback(
    (value: number) => {
      if (!mountedRef.current) return;
      setIsWeekValid(
        props.value.getFullYear().toString().length < 4 ||
          (value >= minWeekAllowed && value <= maxWeekAllowed)
      );
    },
    [mountedRef, props.value, minWeekAllowed, maxWeekAllowed]
  );

  const refSelect = useRef<HTMLDivElement>(null);
  const refInput = useRef<IntegerInputRef>(null);
  useImperativeHandle(ref, () => ({
    isValid: () => !!refInput.current?.isValid() && isWeekValid,
    focus: () => (!isWeekValid ? refSelect.current?.focus() : refInput.current?.focus()),
  }));

  const [isYearValid, setIsYearValid] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      refSelect.current?.focus();
    });
    refSelect.current?.setAttribute('data-autofocus', 'true');
  }, []);

  // update when year is committed
  // update when dropdown is being used
  useEffect(() => {
    // Need to briefly delay validation when opening the dialog so the error message is displayed in
    // the proper location if invalid.  This small delay ensures the dialog is opened and controls are visible.
    setTimeout(() => {
      validateWeekValue(weekNumber);
      setIsYearValid(refInput.current?.isValid() ?? true);
    }, 0);
  }, [weekNumber, validateWeekValue, locale]);

  const yearElement = (
    <YearInput
      testId={postfixTestId(props.testId, 'yearInput')}
      ref={refInput}
      value={getYearOfWeek(props.value, locale)}
      onChange={(nextYear) => {
        const nextValue = getDateByWeekNumber(
          weekNumber,
          isThaiLocale(locale) ? convertThaiYearToGregorianYear(nextYear) : nextYear
        );
        const isValidDate = !isNaN(nextValue.getTime());
        if (!isValidDate) {
          setIsYearValid(false);
          return;
        }
        props.onChange(nextValue);
      }}
      minDate={minDate}
      maxDate={maxDate}
    />
  );

  return (
    <Flow spacing="s" orientation="horizontal">
      {yearOrder === 'before' && yearElement}
      <SelectWithLabels
        testId={postfixTestId(props.testId, 'weekSelect')}
        selectBoxRef={refSelect}
        selectedIndex={weekNumber - 1}
        values={[...Array(numberOfWeeks)].map((_, i) => ({ text: String(i + 1) }))}
        onSelected={(selectedIndex) => {
          const year = getYearOfWeek(props.value, 'none');
          const weekNumber = (selectedIndex ?? 0) + 1;
          props.onChange(getDateByWeekNumber(weekNumber, year));
        }}
        message={!isWeekValid ? minMaxWeekErrorMsg : undefined}
        status={isYearValid && !isWeekValid ? 'alert' : undefined}
        disabled={!isYearValid}
        label={localize('week.label')}
        labelPosition="top"
        wrapLabel={false}
      />
      {yearOrder === 'after' && yearElement}
    </Flow>
  );
});
